import React, { useEffect } from "react";
import { Outlet, NavLink, useParams, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useClients } from "@/contexts/ClientContext";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import {
  ArrowLeft, LayoutDashboard, FileText, Globe2, Upload, ClipboardList,
  BarChart3, Sparkles, PlugZap, NotebookText, History, Users, Settings as SettingsIcon,
  Linkedin, Facebook, ClipboardCheck, Search, Image as ImageIcon,
  MessageSquare, Star, MapPin, Mail, Music2, Phone, CalendarDays, Gauge, Radar, ShieldAlert,
  Wand2, LibraryBig, Server, Scale,
} from "lucide-react";
import { ClientLogo } from "@/components/ClientLogo";

const tabs = [
  { to: "", label: "Overview", icon: LayoutDashboard, testid: "tab-overview", end: true },
  { to: "strategy-v2", label: "Strategy", icon: Sparkles, testid: "tab-strategy-v2" },
  { to: "proposals", label: "Proposals", icon: FileText, testid: "tab-proposals" },
  { to: "strategy", label: "Strategy Planner", icon: FileText, testid: "tab-strategy" },
  { to: "calendar", label: "Calendar", icon: CalendarDays, testid: "tab-calendar" },
  { to: "creative", label: "Creative", icon: Wand2, testid: "tab-creative" },
  { to: "library", label: "Library", icon: LibraryBig, testid: "tab-library" },
  { to: "brief", label: "Brief & Budget", icon: NotebookText, testid: "tab-brief" },
  { to: "contacts", label: "Contacts", icon: Users, testid: "tab-contacts" },
  { to: "legal", label: "Legal", icon: Scale, testid: "tab-legal" },
  { to: "domains", label: "Domains", icon: Globe2, testid: "tab-domains" },
  { to: "hosting", label: "Hosting", icon: Server, testid: "tab-hosting" },
  { to: "locations", label: "Locations", icon: MapPin, testid: "tab-locations" },
  { to: "properties", label: "Properties", icon: Globe2, testid: "tab-properties" },
  { to: "uploads", label: "CSV Uploads", icon: Upload, testid: "tab-uploads" },
  { to: "historical", label: "Historical Stats", icon: History, testid: "tab-historical" },
  { to: "worklogs", label: "Worklogs", icon: ClipboardList, testid: "tab-worklogs" },
  { to: "charts", label: "Charts", icon: BarChart3, testid: "tab-charts" },
  { to: "reports", label: "Reports", icon: FileText, testid: "tab-reports" },
  { to: "ai", label: "AI Assistant", icon: Sparkles, testid: "tab-ai" },
  { to: "connections", label: "API Connections", icon: PlugZap, testid: "tab-connections" },
  { to: "google-feeds", label: "Google Feeds", icon: Globe2, testid: "tab-google-feeds" },
  { to: "bing-feeds", label: "Bing Feeds", icon: Search, testid: "tab-bing-feeds" },
  { to: "ahrefs-feeds", label: "AHREFS Feeds", icon: Search, testid: "tab-ahrefs-feeds" },
  { to: "psi", label: "Google PSI", icon: Gauge, testid: "tab-psi" },
  { to: "woorank", label: "Woorank SEO", icon: Radar, testid: "tab-woorank" },
  { to: "moz-data", label: "MOZ Data", icon: BarChart3, testid: "tab-moz-data" },
  { to: "wordfence", label: "Wordfence", icon: ShieldAlert, testid: "tab-wordfence" },
  { to: "openai-ads", label: "OpenAI", icon: Sparkles, testid: "tab-openai-ads" },
  { to: "linkedin", label: "LinkedIn", icon: Linkedin, testid: "tab-linkedin" },
  { to: "meta-ads", label: "Meta", icon: Facebook, testid: "tab-meta-ads" },
  { to: "microsoft", label: "Microsoft / Bing", icon: Search, testid: "tab-microsoft" },
  { to: "pinterest", label: "Pinterest", icon: ImageIcon, testid: "tab-pinterest" },
  { to: "tiktok", label: "TikTok", icon: Music2, testid: "tab-tiktok" },
  { to: "google-reviews", label: "Google Reviews", icon: Star, testid: "tab-google-reviews" },
  { to: "product-review", label: "ProductReview", icon: MessageSquare, testid: "tab-product-review" },
  { to: "trustpilot", label: "Trustpilot", icon: Star, testid: "tab-trustpilot" },
  { to: "sendgrid", label: "SendGrid", icon: Mail, testid: "tab-sendgrid" },
  { to: "phones", label: "Phones", icon: Phone, testid: "tab-phones" },
  { to: "checklist", label: "Checklist", icon: ClipboardCheck, testid: "tab-checklist" },
  { to: "settings", label: "Settings", icon: SettingsIcon, testid: "tab-settings" },
];

export default function ClientDetailLayout() {
  const { clientId } = useParams();
  const navigate = useNavigate();
  const { clients, selectedId, select, reload } = useClients();
  const { user } = useAuth();

  // Sync URL param into ClientContext so all child pages see the right client
  useEffect(() => {
    if (!clientId) return;
    if (selectedId !== clientId) select(clientId);
  }, [clientId, selectedId, select]);

  // If clients list is empty, fetch fresh
  useEffect(() => {
    if (!clients?.length) reload();
  }, []);

  const client = clients.find((c) => c.client_id === clientId);

  if (!client) {
    return (
      <div className="text-zinc-500 label-overline">Loading client…</div>
    );
  }

  const isAdmin = user?.role === "admin";

  return (
    <div className="space-y-6" data-testid="client-detail">
      {/* Header */}
      <div className="flex items-start justify-between gap-6 print:hidden">
        <div className="flex items-center gap-5 min-w-0">
          <Button variant="ghost" size="sm" className="rounded-sm flex-shrink-0"
            onClick={() => navigate(isAdmin ? "/clients" : "/portal")}
            data-testid="back-to-clients">
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <ClientLogo name={client.company_name} logoUrl={client.logo_url} size={56}
            className="text-2xl" />
          <div className="min-w-0">
            <h1 className="font-display font-extrabold text-3xl tracking-tight truncate">{client.company_name}</h1>
          </div>
        </div>
      </div>

      {/* Tabs — wrap onto multiple rows instead of the old sideways
          scrollbar. Active row highlights use a bottom-border on each
          tab so wrapped rows still read as one cohesive nav strip. */}
      <div className="border-b border-zinc-200 print:hidden">
        <nav className="flex flex-wrap gap-x-1 gap-y-0" data-testid="client-tabs">
          {tabs.map((t) => (
            <NavLink
              key={t.to}
              to={t.to}
              end={t.end}
              data-testid={t.testid}
              className={({ isActive }) =>
                `inline-flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors
                 ${isActive
                   ? "border-[#002FA7] text-[#002FA7]"
                   : "border-transparent text-zinc-600 hover:text-zinc-900 hover:border-zinc-300"}`
              }
            >
              <t.icon className="h-3.5 w-3.5" />
              <span>{t.label}</span>
            </NavLink>
          ))}
        </nav>
      </div>

      {/* Tab content */}
      <Outlet />
    </div>
  );
}
